import { useCallback, useState } from 'react';
import type { FirebaseApp } from 'firebase/app';

import { isConsentRequiredRegion } from './consent-region';
import {
  readConsentDecision,
  writeConsentDecision,
  type ConsentDecision,
} from './consent-store';
import { startTelemetry } from './telemetry';

type ConsentStatus =
  | 'not-required' // Outside EU/UK → telemetry runs, no banner.
  | 'granted' // Consent required and given (now or previously).
  | 'denied' // Consent required and refused.
  | 'undecided'; // Consent required, no decision yet → show banner.

/**
 * Resolve the initial status and start telemetry for the paths that don't
 * need a prompt. Pure enough to unit-test by injecting `required`/`stored`.
 */
export function resolveInitialConsent(
  app: FirebaseApp | null,
  {
    required = isConsentRequiredRegion(),
    stored = readConsentDecision(),
  }: { required?: boolean; stored?: ConsentDecision | null } = {},
): ConsentStatus {
  if (!required) {
    startTelemetry(app);
    return 'not-required';
  }
  if (stored === 'granted') {
    startTelemetry(app);
    return 'granted';
  }
  if (stored === 'denied') {
    return 'denied';
  }
  return 'undecided';
}

export function useConsent(app: FirebaseApp | null) {
  const [status, setStatus] = useState<ConsentStatus>(() =>
    resolveInitialConsent(app),
  );

  const decide = useCallback(
    (decision: ConsentDecision) => {
      writeConsentDecision(decision, new Date().toISOString());
      if (decision === 'granted') {
        startTelemetry(app);
      }
      setStatus(decision);
    },
    [app],
  );

  return {
    // The banner shows only while a required decision is still pending.
    shouldPrompt: status === 'undecided',
    status,
    acceptAll: () => decide('granted'),
    rejectAll: () => decide('denied'),
  };
}
